/*
  Scroll lock for the cart drawer.

  `overflow: hidden` on <body> does not stop scrolling in iOS Safari, so the
  page keeps moving behind the open drawer. The body is pinned with
  position: fixed at the current offset instead, and restored on close so the
  shopper lands back where they were. A touchmove guard blocks rubber-banding
  on anything outside the drawer's own scroller.
*/
const CartDrawerScrollLock = {
  y: 0,
  locked: false,

  onTouchMove(e) {
    const drawer = document.querySelector('cart-drawer.active');
    if (!drawer) return;
    if (!drawer.contains(e.target)) e.preventDefault();
  },

  lock() {
    if (this.locked) return;
    this.locked = true;
    this.y = window.pageYOffset || document.documentElement.scrollTop || 0;
    const b = document.body;
    b.style.position = 'fixed';
    b.style.top = `-${this.y}px`;
    b.style.left = '0';
    b.style.right = '0';
    b.style.width = '100%';
    b.style.overflow = 'hidden';
    b.classList.add('cart-drawer-locked');
    document.addEventListener('touchmove', this._bound || (this._bound = this.onTouchMove.bind(this)), {
      passive: false,
    });
  },

  unlock() {
    if (!this.locked) return;
    this.locked = false;
    const b = document.body;
    b.style.position = '';
    b.style.top = '';
    b.style.left = '';
    b.style.right = '';
    b.style.width = '';
    b.style.overflow = '';
    b.classList.remove('cart-drawer-locked');
    if (this._bound) document.removeEventListener('touchmove', this._bound, { passive: false });
    /* 'instant' — a smooth scroll here reads as the page jumping after close. */
    window.scrollTo({ top: this.y, behavior: 'instant' });
  },
};

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    // Disable drawer trigger on the cart page — let the link navigate normally
    if (document.body.classList.contains('page-cart')) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    CartDrawerScrollLock.lock();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    CartDrawerScrollLock.unlock();
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, open = true) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');

    /* The host element keeps its own is-empty, and this only ever cleared the
       one on .drawer__inner. So after adding to a cart that was empty when the
       page loaded (or after Clear Cart), <cart-drawer> still carried is-empty:
       the empty-state rules collapsed the item list to zero height and the
       drawer slid open looking blank. */
    const count = typeof parsedState.item_count === 'number'
      ? parsedState.item_count
      : (Array.isArray(parsedState.items) ? parsedState.items.length : null);

    if (count !== null) {
      this.classList.toggle('is-empty', count === 0);
      const inner = this.querySelector('.drawer__inner');
      if (inner) inner.classList.toggle('is-empty', count === 0);
    } else {
      this.classList.remove('is-empty');
    }
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      if (open) {
        this.open();
      }
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-quick',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-icon-quick',
        section: 'cart-icon-quick',
        selector: '.shopify-section',
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);